import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  ContentLayout,
  Form,
  Header,
  Icon,
  Link,
  SpaceBetween,
  TextContent,
} from '@amzn/awsui-components-react';
import { useIntl } from 'react-intl';

import { PostAuthComponentParams } from '../../types/auth';
import scopeConsentMessages from '../../i18n/scopeConsent.messages';
import { getConsentConfigForRequest } from '../../constants/scopeConsent';
import { redirectConsentDenied } from '../../services/scopeConsentDeny';
import {
  AWS_CUSTOMER_AGREEMENT,
  T_AND_C_PRIVACY,
  T_AND_C_TERMS,
} from '../../constants/links';
import metricsService from '../../services/metrics';
import { IMPRESSIONS } from '../../constants/metrics';

// How long the "Access granted" success alert stays visible before the flow continues.
const GRANTED_CONTINUE_DELAY_MS = 1500;

// A single permission row: a positive status icon, a bold title, and a description.
function PermissionItem({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div style={{ display: 'flex', gap: '8px' }}>
      <Icon name="status-positive" variant="subtle" />
      <SpaceBetween direction="vertical" size="xxxs">
        <Box variant="strong">{title}</Box>
        <Box variant="small" color="text-body-secondary">
          {description}
        </Box>
      </SpaceBetween>
    </div>
  );
}

const ScopeConsent = ({
  user,
  onContinue,
  onCancel,
  config,
}: PostAuthComponentParams): JSX.Element => {
  const { formatMessage } = useIntl();
  const [showConsent, setShowConsent] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [granted, setGranted] = useState(false);
  const metricsPublisher = useRef(metricsService.getPublisher('ScopeConsent'));
  const continueTimer = useRef<ReturnType<typeof setTimeout>>();

  // Resolve the requesting application from the consent-required scope in the request URL.
  const consentConfig = getConsentConfigForRequest(window.location.search);
  const appName = consentConfig?.appName;

  useEffect(() => {
    // Gate behind a feature flag and the presence of a consent-required scope.
    if (
      config.enableScopeConsentPostAuthFlow !== 'true' ||
      consentConfig === undefined
    ) {
      onContinue();
      return;
    }
    setShowConsent(true);
    metricsPublisher.current.publishCounterMonitor(IMPRESSIONS, 1);
    metricsPublisher.current.publishCounterMonitor(
      `Scope:${consentConfig.scope}`,
      1
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear any pending continue timer if the component unmounts first.
  useEffect(
    () => () => {
      if (continueTimer.current) {
        clearTimeout(continueTimer.current);
      }
    },
    []
  );

  const handleAllow = () => {
    metricsPublisher.current.publishCounterMonitor('Allow', 1);
    // Show the brief "Access granted" state, then continue the normal (BAU) post-auth flow.
    // The Allow button is disabled once granted, so this cannot run twice.
    setGranted(true);
    continueTimer.current = setTimeout(onContinue, GRANTED_CONTINUE_DELAY_MS);
  };

  const handleDeny = () => {
    metricsPublisher.current.publishCounterMonitor('Deny', 1);
    // The Cancel button is disabled once granted, so this cannot run after Allow.
    // Return the user to the destination with error=Consent denied. If there is no valid/safe
    // redirect_uri to return to, fall back to onCancel() (logout) so the user exits the flow —
    // re-authenticating would reload the same consent-scoped URL and loop back to this screen.
    if (!redirectConsentDenied(window.location.search)) {
      onCancel();
    }
  };

  const handleSwitchAccount = (e: CustomEvent) => {
    e.preventDefault();
    // Ignore switch-account once consent is already granted and the continue is pending.
    if (granted) {
      return;
    }
    metricsPublisher.current.publishCounterMonitor('SwitchAccount', 1);
    // Log the user out (via onCancel) so they can sign in as a different account. Re-authenticating
    // the current provider would silently reuse the active session and loop back to this screen.
    onCancel();
  };

  if (!showConsent || !appName) {
    return <></>;
  }

  return (
    <ContentLayout>
      <Container
        header={
          <Header variant="h1" data-testid="scope-consent-header">
            {formatMessage(scopeConsentMessages.header, { appName })}
          </Header>
        }
      >
        <SpaceBetween direction="vertical" size="l">
          <TextContent>
            {formatMessage(scopeConsentMessages.signedInAs, {
              email: user.email,
              b: (chunks) => <b>{chunks}</b>,
            })}
            <Link
              variant="primary"
              onFollow={handleSwitchAccount}
              data-testid="scope-consent-switch-account"
            >
              {formatMessage(scopeConsentMessages.switchAccount)}
            </Link>
          </TextContent>

          <Box variant="strong">
            {formatMessage(scopeConsentMessages.permissionsIntro)}
          </Box>

          <SpaceBetween direction="vertical" size="s">
            <PermissionItem
              title={formatMessage(scopeConsentMessages.permissionProfileTitle)}
              description={formatMessage(
                scopeConsentMessages.permissionProfileDescription
              )}
            />
            <PermissionItem
              title={formatMessage(
                scopeConsentMessages.permissionProgressTitle
              )}
              description={formatMessage(
                scopeConsentMessages.permissionProgressDescription
              )}
            />
            <PermissionItem
              title={formatMessage(
                scopeConsentMessages.permissionCertificationsTitle
              )}
              description={formatMessage(
                scopeConsentMessages.permissionCertificationsDescription
              )}
            />
            <PermissionItem
              title={formatMessage(scopeConsentMessages.permissionCatalogTitle)}
              description={formatMessage(
                scopeConsentMessages.permissionCatalogDescription
              )}
            />
          </SpaceBetween>

          <Checkbox
            checked={consentChecked}
            onChange={({ detail }) => setConsentChecked(detail.checked)}
            data-testid="scope-consent-checkbox"
          >
            {formatMessage(scopeConsentMessages.consentCheckbox, { appName })}
          </Checkbox>

          <Form>
            <SpaceBetween direction="vertical" size="xs">
              <Button
                variant="primary"
                fullWidth
                onClick={handleAllow}
                disabled={!consentChecked || granted}
                data-testid="scope-consent-allow"
              >
                {formatMessage(scopeConsentMessages.allowButton)}
              </Button>
              <Button
                fullWidth
                onClick={handleDeny}
                disabled={granted}
                data-testid="scope-consent-cancel"
              >
                {formatMessage(scopeConsentMessages.cancelButton)}
              </Button>
            </SpaceBetween>
          </Form>

          {granted && (
            <Alert
              type="success"
              header={formatMessage(scopeConsentMessages.accessGrantedHeader)}
              data-testid="scope-consent-granted"
            >
              {formatMessage(scopeConsentMessages.accessGrantedBody)}
            </Alert>
          )}

          <Box variant="small" color="text-body-secondary">
            {formatMessage(scopeConsentMessages.legalText, {
              customerAgreementLink: (chunks) => (
                <Link href={AWS_CUSTOMER_AGREEMENT} external target="_blank">
                  {chunks}
                </Link>
              ),
              privacyLink: (chunks) => (
                <Link href={T_AND_C_PRIVACY} external target="_blank">
                  {chunks}
                </Link>
              ),
              termsLink: (chunks) => (
                <Link href={T_AND_C_TERMS} external target="_blank">
                  {chunks}
                </Link>
              ),
            })}
          </Box>
        </SpaceBetween>
      </Container>
    </ContentLayout>
  );
};

export default ScopeConsent;
